import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import { useAssignLeadToPdfMutation, useLazySearchVendorQuotationsQuery } from "store/services/vendorQuotationApi";
import { useAssignLeadToExcelMutation, useLazySearchVendorQuotationExcelsQuery } from "store/services/vendorQuotationExcelApi";
import { useLazySearchLeadsQuery } from "store/services/vendorLeadApi";


const AssignLeadModal = ({ open, onClose, type }) => {
  const isPdf = type === "pdf";
  const quotationLabel = isPdf ? "Vendor Quotation" : "Vendor Quotation Excel";
  const quotationIdField = isPdf ? "vendorQuotationId" : "VendorQuotationExcelId";

  const [quotationSearch, setQuotationSearch] = useState("");
  const [quotationResults, setQuotationResults] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [searchVendorQuotations, { isFetching: fetchingPdf }] =
    useLazySearchVendorQuotationsQuery();
  const [searchVendorQuotationExcels, { isFetching: fetchingExcel }] =
    useLazySearchVendorQuotationExcelsQuery();
  const [searchLeads, { isFetching: fetchingLeads }] =
    useLazySearchLeadsQuery();

  const [assignLeadToPdf, { isLoading: assigningPdf }] =
    useAssignLeadToPdfMutation();
  const [assignLeadToExcel, { isLoading: assigningExcel }] =
    useAssignLeadToExcelMutation();

  const isFetchingQuotation = fetchingPdf || fetchingExcel;
  const isAssigning = assigningPdf || assigningExcel;

  useEffect(() => {
    if (!quotationSearch.trim()) {
      setQuotationResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const result = isPdf
        ? await searchVendorQuotations({ q: quotationSearch })
        : await searchVendorQuotationExcels({ q: quotationSearch });
      if (result?.data) setQuotationResults(result.data);
    }, 400);
    return () => clearTimeout(timer);
  }, [quotationSearch, isPdf, searchVendorQuotations, searchVendorQuotationExcels]);

  useEffect(() => {
    if (!leadSearch.trim()) {
      setLeadResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const result = await searchLeads({ q: leadSearch });
      if (result?.data) setLeadResults(result.data);
    }, 400);
    return () => clearTimeout(timer);
  }, [leadSearch, searchLeads]);

  const handleSelectQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setQuotationSearch("");
    setQuotationResults([]);
    setSelectedLeads(quotation.vendorLead || []);
  };

  const handleAddLead = (lead) => {
    if (selectedLeads.find((l) => l._id === lead._id)) return;
    setSelectedLeads((prev) => [...prev, lead]);
    setLeadSearch("");
    setLeadResults([]);
  };

  const handleRemoveLead = (leadId) => {
    setSelectedLeads((prev) => prev.filter((l) => l._id !== leadId));
  };

  const handleSubmit = async () => {
    if (!selectedQuotation) return;
    const payload = {
      id: selectedQuotation._id,
      vendorLead: selectedLeads.map((l) => l._id),
    };
    const result = isPdf
      ? await assignLeadToPdf(payload)
      : await assignLeadToExcel(payload);

    if (result.error) {
      setSnackbar({
        open: true,
        message:
          result.error?.data?.message ||
          `Failed to assign lead to ${quotationLabel}.`,
        severity: "error",
      });
    } else {
      setSnackbar({
        open: true,
        message: `Lead successfully assigned to ${quotationLabel}.`,
        severity: "success",
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setQuotationSearch("");
    setQuotationResults([]);
    setSelectedQuotation(null);
    setLeadSearch("");
    setLeadResults([]);
    setSelectedLeads([]);
    onClose();
  };

  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          Assign Lead to {quotationLabel}
          <IconButton size="small" onClick={handleClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Quotation Search */}
          <Typography variant="subtitle2" gutterBottom>
            Search {quotationLabel}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by ID or title..."
            value={quotationSearch}
            onChange={(e) => {
              setQuotationSearch(e.target.value);
              if (selectedQuotation) setSelectedQuotation(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isFetchingQuotation ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
          />

          {quotationResults.length > 0 && !selectedQuotation && (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mt: 0.5,
                maxHeight: 180,
                overflowY: "auto",
              }}
            >
              <List dense disablePadding>
                {quotationResults.map((q) => (
                  <ListItem
                    key={q._id}
                    button
                    onClick={() => handleSelectQuotation(q)}
                    divider
                  >
                    <ListItemText
                      primary={q[quotationIdField]}
                      secondary={q.title || "No title"}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {selectedQuotation && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {selectedQuotation[quotationIdField]}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedQuotation.title || "No title"}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => setSelectedQuotation(null)}
              >
                Change
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Current Assigned Leads */}
          <Typography variant="subtitle2" gutterBottom>
            Assigned Leads
          </Typography>

          {selectedLeads.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No leads assigned yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              {selectedLeads.map((lead) => (
                <Chip
                  key={lead._id}
                  label={
                    <span>
                      <strong>{lead.name || lead.leadId}</strong>
                      {lead.companyName ? ` · ${lead.companyName}` : ""}
                    </span>
                  }
                  onDelete={() => handleRemoveLead(lead._id)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {/* Lead Search */}
          {selectedQuotation && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1.5 }}>
                Search & Add Leads
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, company, email, ID..."
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: fetchingLeads ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
              />

              {leadResults.length > 0 && (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mt: 0.5,
                    maxHeight: 180,
                    overflowY: "auto",
                  }}
                >
                  <List dense disablePadding>
                    {leadResults.map((lead) => {
                      const alreadyAdded = !!selectedLeads.find(
                        (l) => l._id === lead._id
                      );
                      return (
                        <ListItem
                          key={lead._id}
                          button
                          onClick={() => !alreadyAdded && handleAddLead(lead)}
                          divider
                          disabled={alreadyAdded}
                          sx={{ opacity: alreadyAdded ? 0.5 : 1 }}
                        >
                          <ListItemText
                            primary={`${lead.name || "N/A"} (${lead.leadId})`}
                            secondary={lead.companyName || lead.email || ""}
                          />
                          {alreadyAdded && (
                            <Typography variant="caption" color="text.secondary">
                              Added
                            </Typography>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedQuotation || isAssigning}
            startIcon={
              isAssigning ? (
                <CircularProgress size={14} color="inherit" />
              ) : null
            }
          >
            {isAssigning ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignLeadModal;